import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { api, toast } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Calendar, Loader2, Pencil } from "lucide-react";

const HOLIDAY_TYPES = [
  { value: "festival", label: "Festival Holiday", color: "bg-purple-500" },
  { value: "statutory", label: "Statutory Holiday", color: "bg-blue-500" },
  { value: "business", label: "Business Holiday", color: "bg-green-500" }
];

const DEPARTMENTS = ["Estimation", "Human Resources", "Management", "Projects", "Sales"];
const BRANCHES = ["Chennai", "Hyderabad"];

export default function HolidayCalendar({ user }) {
  // Only HR and Management departments can add/edit/delete holidays
  const canManageHolidays = user?.department === "Human Resources" 
    || user?.department === "HR" 
    || user?.department === "Management"
    || user?.access_level === "Admin";

  const [holidays, setHolidays] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [holidayData, setHolidayData] = useState({
    date: "",
    name: "",
    type: "statutory",
    applicability: "all",
    applicable_departments: [],
    applicable_branches: []
  });

  useEffect(() => {
    fetchHolidays();
  }, [year]);

  const fetchHolidays = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/leave/holidays/${year}`);
      setHolidays(response.data.holidays || []);
    } catch (error) {
      toast.error("Failed to fetch holidays");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHoliday = async (e) => {
    e.preventDefault();
    
    // Validate specific applicability
    if (holidayData.applicability === "specific") {
      if (holidayData.applicable_departments.length === 0 && holidayData.applicable_branches.length === 0) {
        toast.error("Please select at least one department or branch");
        return;
      }
    }

    let updatedHolidays;
    if (editIndex !== null) {
      // Update existing
      updatedHolidays = holidays.map((h, i) => i === editIndex ? holidayData : h);
    } else {
      // Add new
      updatedHolidays = [...holidays, holidayData];
    }
    
    // Sort by date
    updatedHolidays.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    try {
      await api.post("/leave/holidays", null, {
        params: { year, holidays: JSON.stringify(updatedHolidays) }
      });
      toast.success(editIndex !== null ? "Holiday updated successfully" : "Holiday added successfully");
      closeDialog();
      fetchHolidays();
    } catch (error) {
      toast.error("Failed to save holiday");
    }
  };

  const handleDeleteHoliday = async (index) => {
    if (!confirm("Are you sure you want to delete this holiday?")) {
      return;
    }

    const updatedHolidays = holidays.filter((_, i) => i !== index);
    
    try {
      await api.post("/leave/holidays", null, {
        params: { year, holidays: JSON.stringify(updatedHolidays) }
      });
      toast.success("Holiday deleted successfully");
      fetchHolidays();
    } catch (error) {
      toast.error("Failed to delete holiday");
    }
  };

  const openAddDialog = () => {
    setEditIndex(null);
    setHolidayData({
      date: "",
      name: "",
      type: "statutory",
      applicability: "all",
      applicable_departments: [],
      applicable_branches: []
    });
    setDialogOpen(true);
  };

  const openEditDialog = (index) => {
    const holiday = holidays[index];
    setEditIndex(index);
    setHolidayData({
      date: holiday.date || "",
      name: holiday.name || "",
      type: holiday.type || "statutory",
      applicability: (holiday.applicable_departments?.length > 0 || holiday.applicable_branches?.length > 0) 
        ? "specific" 
        : (holiday.applicability || "all"),
      applicable_departments: holiday.applicable_departments || [],
      applicable_branches: holiday.applicable_branches || []
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditIndex(null);
    setHolidayData({
      date: "",
      name: "",
      type: "statutory",
      applicability: "all",
      applicable_departments: [],
      applicable_branches: []
    });
  };

  const toggleDepartment = (dept) => {
    setHolidayData(prev => ({
      ...prev,
      applicable_departments: prev.applicable_departments.includes(dept)
        ? prev.applicable_departments.filter(d => d !== dept)
        : [...prev.applicable_departments, dept]
    }));
  };

  const toggleBranch = (branch) => {
    setHolidayData(prev => ({
      ...prev,
      applicable_branches: prev.applicable_branches.includes(branch)
        ? prev.applicable_branches.filter(b => b !== branch)
        : [...prev.applicable_branches, branch]
    }));
  };

  const getTypeConfig = (type) => {
    return HOLIDAY_TYPES.find(t => t.value === type) || HOLIDAY_TYPES[0];
  };

  const formatApplicability = (holiday) => {
    const depts = holiday.applicable_departments || [];
    const branches = holiday.applicable_branches || [];
    
    if (holiday.applicability === "all" || (depts.length === 0 && branches.length === 0)) {
      return <Badge variant="outline" className="bg-gray-100">All</Badge>;
    }
    
    const parts = [];
    if (depts.length > 0) {
      parts.push(
        <span key="depts" className="flex flex-wrap gap-1">
          {depts.map(d => (
            <Badge key={d} variant="outline" className="bg-blue-50 text-blue-700 text-xs">
              {d}
            </Badge>
          ))}
        </span>
      );
    }
    if (branches.length > 0) {
      parts.push(
        <span key="branches" className="flex flex-wrap gap-1">
          {branches.map(b => (
            <Badge key={b} variant="outline" className="bg-green-50 text-green-700 text-xs">
              {b}
            </Badge>
          ))}
        </span>
      );
    }
    
    return <div className="flex flex-col gap-1">{parts}</div>;
  };

  const getHolidayStats = () => {
    const stats = { festival: 0, statutory: 0, business: 0 };
    holidays.forEach(h => {
      if (stats[h.type] !== undefined) stats[h.type]++;
    });
    return stats;
  };

  const stats = getHolidayStats();

  return (
    <Layout user={user}>
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-md3-display-small">Holiday Calendar</h1>
            <p className="text-md3-on-surface-variant">Manage company holidays</p>
          </div>
          <div className="flex gap-2 items-center">
            <Select value={year.toString()} onValueChange={(val) => setYear(parseInt(val))}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2024">2024</SelectItem>
                <SelectItem value="2025">2025</SelectItem>
                <SelectItem value="2026">2026</SelectItem>
              </SelectContent>
            </Select>
            <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={openAddDialog} disabled={!canManageHolidays} data-testid="add-holiday-btn" style={!canManageHolidays ? {display: 'none'} : undefined}>
              <Plus className="h-4 w-4 mr-2" />
              Add Holiday
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-purple-500 shadow-md3-1">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-md3-body-small text-md3-on-surface-variant">Total Holidays</p>
                  <p className="text-md3-display-small">{holidays.length}</p>
                </div>
                <Calendar className="h-10 w-10 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-purple-400 shadow-md3-1">
            <CardContent className="pt-6">
              <div>
                <p className="text-md3-body-small text-md3-on-surface-variant">Festival Holidays</p>
                <p className="text-2xl font-bold text-purple-600">{stats.festival}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-blue-500 shadow-md3-1">
            <CardContent className="pt-6">
              <div>
                <p className="text-md3-body-small text-md3-on-surface-variant">Statutory Holidays</p>
                <p className="text-2xl font-bold text-blue-600">{stats.statutory}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-green-500 shadow-md3-1">
            <CardContent className="pt-6">
              <div>
                <p className="text-md3-body-small text-md3-on-surface-variant">Business Holidays</p>
                <p className="text-2xl font-bold text-green-600">{stats.business}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
          <CardHeader>
            <CardTitle className="text-md3-title-large text-md3-on-surface">Holidays List - {year}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
              </div>
            ) : holidays.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-md3-on-surface-variant">No holidays added for {year}</p>
              </div>
            ) : (
              <Table className="bg-md3-surface-container rounded-md3-md">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-32">Date</TableHead>
                    <TableHead>Holiday Name</TableHead>
                    <TableHead className="w-40">Type</TableHead>
                    <TableHead>Applicable To</TableHead>
                    {canManageHolidays && <TableHead className="w-24">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {holidays.map((holiday, index) => {
                    const typeConfig = getTypeConfig(holiday.type);
                    return (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{holiday.date}</TableCell>
                        <TableCell>{holiday.name}</TableCell>
                        <TableCell>
                          <Badge className={`${typeConfig.color} text-white`}>
                            {typeConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatApplicability(holiday)}</TableCell>
                        {canManageHolidays && (
                        <TableCell>
                          <div className="flex gap-2">
                            <Button 
                              size="sm"
                              variant="outline"
                              className="h-8 w-8 p-0"
                              onClick={() => openEditDialog(index)}
                              data-testid={`holiday-edit-${index}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm"
                              variant="destructive"
                              className="h-8 w-8 p-0 bg-red-600 hover:bg-red-700"
                              onClick={() => handleDeleteHoliday(index)}
                              data-testid={`holiday-delete-${index}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Holiday Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => !open && closeDialog()}>
          <DialogContent className="max-w-lg bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold">
                {editIndex !== null ? "Edit Holiday" : "Add Holiday"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveHoliday} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date" className="text-md3-label-large text-gray-700 font-medium">Date *</Label>
                  <Input 
                    className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20" 
                    id="date"
                    type="date"
                    value={holidayData.date}
                    onChange={(e) => setHolidayData({...holidayData, date: e.target.value})}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="type" className="text-md3-label-large text-gray-700 font-medium">Type *</Label>
                  <Select
                    value={holidayData.type}
                    onValueChange={(value) => setHolidayData({...holidayData, type: value})}
                  >
                    <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HOLIDAY_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <span className={`w-3 h-3 rounded-full ${type.color}`}></span>
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name" className="text-md3-label-large text-gray-700 font-medium">Holiday Name *</Label>
                <Input 
                  className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20" 
                  id="name"
                  value={holidayData.name}
                  onChange={(e) => setHolidayData({...holidayData, name: e.target.value})}
                  placeholder="e.g., Independence Day"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label className="text-md3-label-large text-gray-700 font-medium">Applicable To *</Label>
                <Select
                  value={holidayData.applicability}
                  onValueChange={(value) => {
                    setHolidayData({
                      ...holidayData, 
                      applicability: value,
                      applicable_departments: value === "all" ? [] : holidayData.applicable_departments,
                      applicable_branches: value === "all" ? [] : holidayData.applicable_branches
                    });
                  }}
                >
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All (Everyone)</SelectItem>
                    <SelectItem value="specific">Specific Departments / Branches</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {holidayData.applicability === "specific" && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  <div className="space-y-3">
                    <Label className="text-md3-label-large text-gray-700 font-medium flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                      Departments
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      {DEPARTMENTS.map(dept => (
                        <div key={dept} className="flex items-center space-x-2">
                          <Checkbox
                            id={`dept-${dept}`}
                            checked={holidayData.applicable_departments.includes(dept)}
                            onCheckedChange={() => toggleDepartment(dept)}
                          />
                          <label
                            htmlFor={`dept-${dept}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                          >
                            {dept}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-md3-label-large text-gray-700 font-medium flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-green-500"></span>
                      Branches
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      {BRANCHES.map(branch => (
                        <div key={branch} className="flex items-center space-x-2">
                          <Checkbox
                            id={`branch-${branch}`}
                            checked={holidayData.applicable_branches.includes(branch)}
                            onCheckedChange={() => toggleBranch(branch)}
                          />
                          <label
                            htmlFor={`branch-${branch}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                          >
                            {branch}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <p className="text-xs text-gray-500 mt-2">
                    Holiday will apply to users matching any selected department OR branch.
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" 
                  onClick={closeDialog}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                >
                  {editIndex !== null ? "Update Holiday" : "Add Holiday"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
